import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  CheckCircle2,
  Coins,
  CreditCard,
  Mail,
  RefreshCw,
  Search,
  Send,
  TrendingUp,
  UserCheck,
  Users,
  Wallet,
} from 'lucide-react';
import GrowthTabNav from './GrowthTabNav';
import {
  getExhaustedCandidates,
  getExhaustedRecruiters,
  getLimitAlertSummary,
  refreshLimitAlerts,
} from '../../Services/GrowthService';
import {
  ExhaustionFilter,
  ExhaustionType,
  LimitAlertSummary,
  LimitCandidateExhaustionResponse,
  LimitExhaustionCandidateStats,
  LimitExhaustionRecruiterStats,
  LimitExhaustionRow,
  LimitRecruiterExhaustionResponse,
} from '../../Types/GrowthType';
import { formatDateTime } from '../../../Shared/Utils/HelperUtils';
import Pagination from '../../../Shared/Pagination/Pagination';
import { PaginationResponse } from '../../../Shared/Pagination/PaginationResponse';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import GiveCreditsModal, { CreditRecipient } from './GiveCreditsModal';
import OutreachModal from './OutreachModal';

const PAGE_SIZE = 10;

const labelClass =
  'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';
const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';
const headerClass = 'px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500';

const MiniStat = ({
  label,
  value,
  sub,
  icon,
  tone = 'slate',
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  tone?: 'slate' | 'cyan' | 'rose' | 'amber' | 'emerald';
}) => {
  const valueTone = {
    slate: 'text-slate-100',
    cyan: 'text-cyan-400',
    rose: 'text-rose-400',
    amber: 'text-amber-400',
    emerald: 'text-emerald-400',
  }[tone];

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-4">
      <span className="flex items-center gap-2 text-xs font-semibold text-slate-400">
        {icon}
        {label}
      </span>
      <p className={`mt-1.5 text-lg font-black ${valueTone}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {sub && <p className="text-[10px] font-semibold text-slate-500">{sub}</p>}
    </div>
  );
};

const rowName = (row: LimitExhaustionRow) =>
  `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || row.email || 'Unknown';

/** "Out of credits" reads very differently for the two roles, so the badge is role-aware. */
const exhaustionBadge = (row: LimitExhaustionRow) => {
  const invite = row.exhaustionTypes.includes('CANDIDATE_INVITE') || row.exhaustionTypes.includes('RECRUITER_INVITE');
  const job = row.exhaustionTypes.includes('RECRUITER_JOB_CREDITS');

  if (invite && job) {
    return {
      label: 'Invite + Job Credits',
      className: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
    };
  }
  if (job) {
    return {
      label: 'Job Credits 0',
      className: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
    };
  }
  return {
    label: 'Invites 0',
    className: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  };
};

const OutreachStateBadge = ({ row }: { row: LimitExhaustionRow }) => {
  if ((row.outreachCount ?? 0) === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-amber-400">
        <Bell size={11} />
        Not contacted
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-400">
      <CheckCircle2 size={11} />
      {row.outreachCount} sent
    </span>
  );
};

const ActionButton = ({
  onClick,
  icon,
  label,
  tone = 'slate',
  disabled,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  tone?: 'slate' | 'amber' | 'cyan';
  disabled?: boolean;
}) => {
  const tones = {
    slate: 'border-slate-700 bg-slate-900 text-slate-300 hover:border-cyan-500/40 hover:text-cyan-400',
    amber: 'border-amber-500/30 bg-amber-500/10 text-amber-400 hover:border-amber-400/60 hover:bg-amber-500/20',
    cyan: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400 hover:border-cyan-400/60 hover:bg-cyan-500/20',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest transition disabled:opacity-40 ${tones[tone]}`}
    >
      {icon}
      {label}
    </button>
  );
};

const RecruitmentCell = ({ row }: { row: LimitExhaustionRow }) => {
  if (row.jobCreditsBalance != null && row.jobCreditsBalance <= 0) {
    return (
      <div className="space-y-1">
        <span className="inline-flex rounded-lg border border-rose-500/30 bg-rose-500/10 px-2.5 py-1 text-xs font-black text-rose-400">
          0
        </span>
        <p className="text-[10px] font-semibold text-slate-500">
          {row.isCompanyAdmin ? 'Company admin' : 'Not company admin'}
        </p>
      </div>
    );
  }
  return (
    <span className="text-xs font-semibold text-slate-600">
      {row.jobCreditsBalance == null ? 'No company' : row.jobCreditsBalance}
    </span>
  );
};

/**
 * One screen for "who has run out of credits", for both roles.
 *
 * <p>Candidates and recruiters are on separate tabs rather than one merged table because the
 * columns that make a row actionable differ: for a candidate it is referral output, for a recruiter
 * it is subscription history and whether anyone has been contacted yet. Merging them would leave
 * most columns empty most of the time.
 */
function GrowthLimitsComponent() {
  const [tab, setTab] = useState<'CANDIDATE' | 'RECRUITER'>('RECRUITER');

  const [summary, setSummary] = useState<LimitAlertSummary | null>(null);
  const [candidateStats, setCandidateStats] = useState<LimitExhaustionCandidateStats | null>(null);
  const [recruiterStats, setRecruiterStats] = useState<LimitExhaustionRecruiterStats | null>(null);

  const [rows, setRows] = useState<LimitExhaustionRow[]>([]);
  const [pagination, setPagination] = useState<PaginationResponse<LimitExhaustionRow> | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('EXHAUSTED_AT');
  const [exhaustionFilter, setExhaustionFilter] = useState<ExhaustionFilter>('ALL');

  // Only keys the backend whitelists in normalizeSort. Anything else is silently coerced to
  // EXHAUSTED_AT, so an unlisted option here would be a control that appears to work and does not.
  const SORT_OPTIONS = useMemo(
    () =>
      tab === 'CANDIDATE'
        ? [
            { value: 'EXHAUSTED_AT', label: 'Longest exhausted' },
            { value: 'REGISTRATIONS', label: 'Referral registrations' },
            { value: 'CREDITS_GRANTED', label: 'Credits given' },
            { value: 'LIFETIME', label: 'Lifetime credits' },
          ]
        : [
            { value: 'EXHAUSTED_AT', label: 'Longest exhausted' },
            { value: 'PLAN', label: 'Plan name' },
            { value: 'OUTREACH', label: 'Times contacted' },
            { value: 'CREDITS_GRANTED', label: 'Credits given' },
          ],
    [tab]
  );

  const [creditRecipient, setCreditRecipient] = useState<CreditRecipient | null>(null);
  const [outreachTarget, setOutreachTarget] = useState<{
    userId: string;
    displayName: string;
    exhaustionType: ExhaustionType | null;
  } | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, sortBy, exhaustionFilter, tab]);

  /**
   * Detection runs server-side on a schedule, but an admin looking at this screen is doing so
   * because someone may have just run dry. Refreshing on open means the list reflects now rather
   * than up to ten minutes ago, and it is also what raises any outstanding admin notifications.
   */
  const runRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const response = await refreshLimitAlerts();
      if (response.success && response.data) {
        setSummary(response.data);
      }
    } catch {
      // A failed sweep should not blank the screen; the scheduled run still applies.
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    runRefresh();
  }, [runRefresh]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response =
        tab === 'CANDIDATE'
          ? await getExhaustedCandidates(
              { search: debouncedSearch || undefined, sortBy, page },
              page,
              PAGE_SIZE
            )
          : await getExhaustedRecruiters(
              {
                search: debouncedSearch || undefined,
                sortBy,
                exhaustionType: exhaustionFilter,
                page,
              },
              page,
              PAGE_SIZE
            );

      if (response.success && response.data) {
        const pageData = response.data.page;
        setRows(pageData.content ?? []);
        setPagination(pageData);
        // Split rather than narrowing in place: both listings share the response envelope but the
        // stats shape is role-specific, so the union cannot be narrowed by the tab alone.
        if (tab === 'CANDIDATE') {
          setCandidateStats(
            (response.data as LimitCandidateExhaustionResponse).stats ?? null
          );
        } else {
          setRecruiterStats(
            (response.data as LimitRecruiterExhaustionResponse).stats ?? null
          );
        }
        if (pageData.totalPages > 0 && page >= pageData.totalPages) {
          setPage(pageData.totalPages - 1);
        }
      } else {
        setRows([]);
        setPagination(null);
        setAlert({
          variant: 'error',
          title: 'Could not load limits',
          message: response.message || 'Please try again.',
          onClose: () => setAlert(null),
        });
      }
    } catch (err) {
      console.error('Failed to load limit data', err);
    } finally {
      setLoading(false);
    }
  }, [tab, debouncedSearch, sortBy, exhaustionFilter, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const reloadAll = () => {
    runRefresh();
    fetchData();
  };

  const headerStats = useMemo(() => {
    if (tab === 'CANDIDATE') {
      if (!candidateStats) return null;
      return [
        { label: 'Candidates Out', value: candidateStats.exhaustedCandidates, sub: `${candidateStats.exhaustedPctOfBase.toFixed(1)}% of all candidates`, icon: <Users size={15} className="text-cyan-400" />, tone: 'cyan' as const },
        { label: 'Out This Week', value: candidateStats.exhaustedLast7Days, sub: `Last 30d: ${candidateStats.exhaustedLast30Days}`, icon: <TrendingUp size={15} className="text-rose-400" />, tone: 'rose' as const },
        { label: 'Brought People In', value: candidateStats.referrerExhaustedCount, sub: `${candidateStats.referrerSharePct.toFixed(1)}% of the exhausted group`, icon: <UserCheck size={15} className="text-emerald-400" />, tone: 'emerald' as const },
        { label: 'Avg Days to Exhaust', value: candidateStats.avgDaysToExhaust.toFixed(1), sub: 'Across the exhausted group', icon: <CalendarClock size={15} className="text-amber-400" />, tone: 'amber' as const },
      ];
    }
    if (!recruiterStats) return null;
    return [
      { label: 'Recruiters Out', value: recruiterStats.exhaustedRecruiters, sub: `${recruiterStats.exhaustedPctOfBase.toFixed(1)}% of all recruiters`, icon: <Users size={15} className="text-cyan-400" />, tone: 'cyan' as const },
      { label: 'Never Paid', value: recruiterStats.neverPaidExhaustedCount, sub: 'Trial only, now at zero', icon: <CreditCard size={15} className="text-rose-400" />, tone: 'rose' as const },
      { label: 'Not Contacted', value: recruiterStats.notYetContactedCount, sub: `Contacted ${recruiterStats.contactedCount} of ${recruiterStats.exhaustedRecruiters}`, icon: <Mail size={15} className="text-amber-400" />, tone: 'amber' as const },
      { label: 'Paid & Blocked', value: recruiterStats.activeSubscriberExhaustedCount, sub: 'Active plan, no credits', icon: <AlertTriangle size={15} className="text-emerald-400" />, tone: 'emerald' as const },
    ];
  }, [tab, candidateStats, recruiterStats]);

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      <GrowthTabNav />

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
          <AlertTriangle size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">
            Limits Exceeded
          </h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Candidates and recruiters who have run out of credits
          </p>
        </div>
        <button
          onClick={reloadAll}
          title="Re-scan for newly exhausted users"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
        >
          <RefreshCw size={16} className={refreshing || loading ? 'animate-spin' : ''} />
          Re-scan
        </button>
      </div>

      {summary && summary.totalOpen > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-4 rounded-2xl border border-rose-500/30 bg-rose-500/5 px-5 py-4 text-xs font-semibold text-slate-300">
          <span className="flex items-center gap-2 text-rose-400">
            <AlertTriangle size={14} />
            {summary.totalOpen} open
          </span>
          <span>
            {summary.candidateExhausted} candidate{summary.candidateExhausted === 1 ? '' : 's'} ·{' '}
            {summary.recruiterExhausted} recruiter
            {summary.recruiterExhausted === 1 ? '' : 's'}
          </span>
          {summary.recruiterNotContacted > 0 && (
            <span className="text-amber-400">
              {summary.recruiterNotContacted} recruiter
              {summary.recruiterNotContacted === 1 ? '' : 's'} never contacted
            </span>
          )}
          {summary.unnotifiedCount > 0 && (
            <span className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-amber-400">
              <Bell size={11} />
              {summary.unnotifiedCount} new
            </span>
          )}
        </div>
      )}

      {/* Role tabs */}
      <div className="mb-6 flex gap-2 rounded-2xl border border-slate-800 bg-slate-900/60 p-2">
        {(
          [
            {
              key: 'RECRUITER' as const,
              label: 'Recruiters',
              count: summary?.recruiterExhausted ?? null,
              icon: <Users size={14} />,
            },
            {
              key: 'CANDIDATE' as const,
              label: 'Candidates',
              count: summary?.candidateExhausted ?? null,
              icon: <UserCheck size={14} />,
            },
          ]
        ).map((option) => (
          <button
            key={option.key}
            onClick={() => setTab(option.key)}
            className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-wider transition ${
              tab === option.key
                ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950 shadow-lg shadow-cyan-500/20'
                : 'border border-slate-800 bg-slate-950/60 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-400'
            }`}
          >
            {option.icon}
            {option.label}
            {option.count !== null && (
              <span
                className={`rounded-md px-1.5 py-0.5 text-[10px] font-black ${
                  tab === option.key ? 'bg-slate-950/20 text-slate-950' : 'bg-slate-800 text-slate-300'
                }`}
              >
                {option.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-4 lg:flex-row lg:items-end">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name / email..."
            className={`${fieldClass} pl-9`}
          />
        </div>
        <div>
          <label className={labelClass}>Sort</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={fieldClass}>
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {tab === 'RECRUITER' && (
          <div>
            <label className={labelClass}>Out Of</label>
            <select
              value={exhaustionFilter}
              onChange={(e) => setExhaustionFilter(e.target.value as ExhaustionFilter)}
              className={fieldClass}
            >
              <option value="ALL">Anything</option>
              <option value="INVITE">Invite credits only</option>
              <option value="JOB_CREDITS">Job credits only</option>
              <option value="BOTH">Both</option>
            </select>
          </div>
        )}
      </div>

      {headerStats && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {headerStats.map((stat) => (
            <MiniStat key={stat.label} {...stat} />
          ))}
        </div>
      )}

      {/* Candidate table */}
      {tab === 'CANDIDATE' && (
        <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
          <div className="border-b border-slate-800 bg-slate-900/60 px-5 py-4">
            <h3 className="text-sm font-black uppercase tracking-widest text-slate-300">
              Candidates Out Of Invites
            </h3>
            <p className="mt-1 text-[11px] font-semibold text-slate-500">
              Referral output is kept per referrer so a top referrer at zero can be spotted before
              they go quiet.
            </p>
          </div>
          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center gap-2 px-5 py-12 text-sm font-semibold text-slate-400">
              <RefreshCw size={16} className="animate-spin text-cyan-500" />
              Loading candidates...
            </div>
          ) : (
            <table className="w-full min-w-[1000px] text-left">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/40">
                  {[
                    'Candidate',
                    'Balance',
                    'Links',
                    'Registered',
                    'Approved',
                    'Out Since',
                    'Alerts',
                    'Action',
                  ].map((h) => (
                    <th key={h} className={headerClass}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {rows.map((row, idx) => {
                  const badge = exhaustionBadge(row);
                  return (
                    <motion.tr
                      key={row.userId}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      className="transition hover:bg-slate-900/70"
                    >
                      <td className="px-5 py-4">
                        <p className="text-sm font-bold text-slate-100">{rowName(row)}</p>
                        <p className="text-[11px] font-semibold text-slate-500">{row.email || '—'}</p>
                        <span
                          className={`mt-1.5 inline-flex rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1 rounded-lg border border-rose-500/30 bg-rose-500/10 px-2.5 py-1 text-xs font-black text-rose-400">
                          <Wallet size={11} />
                          0
                          {row.inviteTotal != null && row.inviteTotal > 0 && (
                            <span className="text-slate-500">/{row.inviteTotal}</span>
                          )}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm font-semibold text-slate-200">
                        {row.totalLinks ?? 0}
                        <span className="ml-1 text-[11px] font-semibold text-slate-500">
                          ({row.activeLinks ?? 0} open)
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm font-semibold text-slate-300">
                        {row.registeredCount ?? 0}
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-flex rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-bold text-emerald-400">
                          {row.approvedCount ?? 0}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-xs font-semibold text-slate-400">
                          {formatDateTime(row.exhaustedAt ?? undefined) || '—'}
                        </p>
                        {row.daysSinceExhausted != null && (
                          <p className="text-[10px] font-semibold text-slate-600">
                            {row.daysSinceExhausted}d ago
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-4 text-xs font-semibold text-slate-400">
                        {row.notificationCount ?? 0}
                      </td>
                      <td className="px-5 py-4">
                        <ActionButton
                          tone="amber"
                          icon={<Coins size={11} />}
                          label="Give Credits"
                          onClick={() =>
                            setCreditRecipient({
                              userId: row.userId,
                              name: rowName(row),
                              email: row.email,
                              role: 'CANDIDATE',
                              inviteBalance: row.inviteBalance,
                              inviteTotal: row.inviteTotal,
                            })
                          }
                        />
                      </td>
                    </motion.tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-12 text-center text-sm font-semibold text-slate-500">
                      {debouncedSearch
                        ? 'No candidates match that search.'
                        : 'No candidates have run out of invite credits.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Recruiter table */}
      {tab === 'RECRUITER' && (
        <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
          <div className="border-b border-slate-800 bg-slate-900/60 px-5 py-4">
            <h3 className="text-sm font-black uppercase tracking-widest text-slate-300">
              Recruiters Out Of Credits
            </h3>
            <p className="mt-1 text-[11px] font-semibold text-slate-500">
              Outreach state is per recruiter. "Not contacted" is the work queue.
            </p>
          </div>
          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center gap-2 px-5 py-12 text-sm font-semibold text-slate-400">
              <RefreshCw size={16} className="animate-spin text-cyan-500" />
              Loading recruiters...
            </div>
          ) : (
            <table className="w-full min-w-[1200px] text-left">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/40">
                  {[
                    'Recruiter',
                    'Company',
                    'Invites',
                    'Job Credits',
                    'Plan',
                    'Out Of',
                    'Outreach',
                    'Action',
                  ].map((h) => (
                    <th key={h} className={headerClass}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {rows.map((row, idx) => {
                  const badge = exhaustionBadge(row);
                  return (
                    <motion.tr
                      key={row.userId}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      className="transition hover:bg-slate-900/70"
                    >
                      <td className="px-5 py-4">
                        <p className="text-sm font-bold text-slate-100">{rowName(row)}</p>
                        <p className="text-[11px] font-semibold text-slate-500">{row.email || '—'}</p>
                        <span
                          className={`mt-1.5 inline-flex rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm font-semibold text-slate-200">
                          {row.companyName || '—'}
                        </p>
                        <p className="text-[10px] font-semibold text-slate-500">
                          {row.companySizeRange || ''}
                          {row.isCompanyAdmin ? ' · admin' : ''}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-black ${
                            (row.inviteBalance ?? 0) === 0
                              ? 'border-amber-500/30 bg-amber-500/10 text-amber-400'
                              : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300'
                          }`}
                        >
                          {row.inviteBalance ?? 0}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <RecruitmentCell row={row} />
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-xs font-bold text-slate-300">
                          {row.subscriptionPlanName || 'No plan'}
                        </p>
                        <p className="text-[10px] font-semibold text-slate-500">
                          {row.trialUsed ? 'Trial used' : 'No trial'}
                          {row.subscriptionPlanStatus
                            ? ` · ${row.subscriptionPlanStatus}`
                            : ''}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-xs font-semibold text-slate-400">
                          {formatDateTime(row.exhaustedAt ?? undefined) || '—'}
                        </p>
                        {row.daysSinceExhausted != null && (
                          <p className="text-[10px] font-semibold text-slate-600">
                            {row.daysSinceExhausted}d ago
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <OutreachStateBadge row={row} />
                        {row.lastOutreachAt && (
                          <p className="mt-1 text-[10px] font-semibold text-slate-600">
                            {formatDateTime(row.lastOutreachAt)}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-wrap gap-2">
                          <ActionButton
                            tone="cyan"
                            icon={<Send size={11} />}
                            label="Contact"
                            onClick={() =>
                              setOutreachTarget({
                                userId: row.userId,
                                displayName: rowName(row),
                                exhaustionType: row.exhaustionTypes[0] ?? null,
                              })
                            }
                          />
                          <ActionButton
                            tone="amber"
                            icon={<Coins size={11} />}
                            label="Free Credits"
                            onClick={() =>
                              setCreditRecipient({
                                userId: row.userId,
                                name: rowName(row),
                                email: row.email,
                                role: 'RECRUITER',
                                inviteBalance: row.inviteBalance,
                                inviteTotal: row.inviteTotal,
                                jobCreditsBalance: row.jobCreditsBalance,
                              })
                            }
                          />
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-12 text-center text-sm font-semibold text-slate-500">
                      {debouncedSearch
                        ? 'No recruiters match that search.'
                        : 'No recruiters have run out of credits.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {pagination && pagination.totalElements > 0 && (
        <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
          <Pagination
            page={pagination.page}
            size={pagination.size}
            totalElements={pagination.totalElements}
            totalPages={pagination.totalPages}
            first={pagination.first}
            last={pagination.last}
            hasNext={pagination.hasNext}
            hasPrevious={pagination.hasPrevious}
            setPage={setPage}
          />
        </div>
      )}

      {recruiterStats && tab === 'RECRUITER' && (
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 rounded-2xl border border-slate-800 bg-slate-900/60 px-5 py-4 text-xs font-semibold text-slate-400">
          <span>Out in last 30d: {recruiterStats.exhaustedLast30Days}</span>
          <span className="text-slate-600">|</span>
          <span>
            Avg days to exhaust: {recruiterStats.avgDaysToExhaust.toFixed(1)}
          </span>
          <span className="text-slate-600">|</span>
          <span>
            Trial expired: {recruiterStats.trialExpiredCount} · paid &amp; blocked:{' '}
            {recruiterStats.paidSubscriberExhaustedCount}
          </span>
          <span className="text-slate-600">|</span>
          <span>
            Credits given so far: {recruiterStats.creditsGranted} across{' '}
            {recruiterStats.refills} grants
          </span>
        </div>
      )}

      <GiveCreditsModal
        recipient={creditRecipient}
        closesAlert
        onClose={() => setCreditRecipient(null)}
        onGranted={(message) => {
          setAlert({
            variant: 'success',
            title: 'Free credits added',
            message,
            onClose: () => setAlert(null),
          });
          reloadAll();
        }}
        onError={(title, message) =>
          setAlert({ variant: 'error', title, message, onClose: () => setAlert(null) })
        }
      />

      <OutreachModal
        userId={outreachTarget?.userId ?? null}
        exhaustionType={outreachTarget?.exhaustionType ?? null}
        displayName={outreachTarget?.displayName ?? ''}
        onClose={() => setOutreachTarget(null)}
        onSent={(recipient) => {
          setAlert({
            variant: 'success',
            title: 'Message sent',
            message: `Outreach recorded for ${recipient}.`,
            onClose: () => setAlert(null),
          });
          fetchData();
        }}
        onError={(title, message) =>
          setAlert({ variant: 'error', title, message, onClose: () => setAlert(null) })
        }
      />
    </div>
  );
}

export default GrowthLimitsComponent;